/**
 * Joining an online booking and copying its passcode (shared-client-screens D4, rule 14), one rule
 * for the app's booking page and the website's. Join copies the passcode first, saying which
 * meeting opens next, then opens the meeting link; the opener, the clipboard and the toast are
 * the caller's, so the app opens through Telegram and the website in a new tab.
 */
import { virtualPlatformLabel } from '@telify/shared/browser';
import type { BookingDetail } from '../types/bookingDetail';

type Toast = (message: string, type: 'success' | 'error' | 'info') => void;

export interface JoinVirtualDeps {
  /** Writes text to the clipboard; rejects when it cannot. */
  copy: (text: string) => Promise<void>;
  toast: Toast;
  open: (url: string) => void;
}

type OnlineLocation = Pick<BookingDetail['location'], 'virtualLink' | 'virtualPassword' | 'virtualPlatform'>;

export async function joinVirtualMeeting(location: OnlineLocation, { copy, toast, open }: JoinVirtualDeps): Promise<void> {
  const link = location.virtualLink;
  if (!link) {
    toast('Meeting link not available yet', 'info');
    return;
  }
  const passcode = location.virtualPassword;
  if (passcode) {
    // The real platform's name; Other has none and reads "the meeting".
    const target = virtualPlatformLabel(location.virtualPlatform) ?? 'the meeting';
    try {
      await copy(passcode);
      toast(`Passcode copied. Opening ${target}…`, 'success');
    } catch {
      toast(`Could not copy the passcode. Opening ${target}…`, 'error');
    }
  }
  open(link);
}

export async function copyPasscode(passcode: string | null, { copy, toast }: Pick<JoinVirtualDeps, 'copy' | 'toast'>): Promise<void> {
  if (!passcode) return;
  try {
    await copy(passcode);
    toast('Passcode copied', 'success');
  } catch {
    toast('Could not copy passcode', 'error');
  }
}

/** The browser's clipboard, for `copy`. */
export const writeClipboard = (text: string): Promise<void> => navigator.clipboard.writeText(text);
